import React from 'react';

export const Card = ({ 
  children, 
  className = '', 
  onClick = null,
  hover = true,
  size = 'md',
  animated = false,
  delay = 0,
  shadowType = null,
  ...props 
}) => {
  const sizeClasses = {
    sm: 'card-sm',
    md: 'card',
    lg: 'card-lg',
  };

  const shadowClass = shadowType ? `card-shadow-${shadowType}` : '';

  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        ${hover ? 'card-hover' : ''} 
        ${animated ? 'animate-fade-in' : ''}
        ${shadowClass}
        ${className}
      `}
      onClick={onClick}
      style={{ animationDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </div>
  );
};

// Accent colors of the AiventraLMS dashboard tiles (DashboardPage StatCard):
// status colors stay the same in light and dark mode, as in the LMS.
export const STAT_COLORS = {
  primary: 'var(--primary-color)',
  success: '#52c41a',
  danger: '#f5222d',
  warning: '#fa8c16',
  info: '#13c2c2',
};

// LMS dashboard tile: 4px colored left border with a matching soft shadow,
// grey title, 22px colored value with its icon, optional caption, and a
// "Click to view →" / "Click to filter" hint when the tile is clickable.
export const StatCard = ({
  title,
  value,
  icon: Icon,
  color = 'primary',
  subtitle = null,
  onClick = null,
  delay = 0,
  compact = false,
  active = false,
  hint = null,
  filterHint = false,
}) => {
  const accent = STAT_COLORS[color] || color || STAT_COLORS.primary;
  const glow = `color-mix(in srgb, ${accent} 20%, transparent)`;

  return (
    <div
      onClick={onClick}
      className={`card stat-card h-full !mb-0 ${compact ? '!p-3' : ''} ${onClick ? 'stat-card-clickable' : ''} ${active ? 'stat-card-active' : ''}`}
      style={{
        borderLeft: `4px solid ${accent}`,
        boxShadow: active ? `0 0 0 2px ${glow}` : `0 2px 8px ${glow}`,
        animationDelay: `${delay}ms`,
      }}
      {...(onClick ? {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': active,
        title: hint || undefined,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } },
      } : {})}
    >
      <div className="stat-card-title">{title}</div>
      <div className="stat-card-value" style={{ color: accent }}>
        {Icon && <Icon className="shrink-0" style={{ fontSize: 20 }} />}
        <span>{value ?? 0}</span>
      </div>
      {subtitle && <div className="stat-card-sub">{subtitle}</div>}
      {onClick && filterHint && (
        <div className="stat-card-sub" style={active ? { color: accent } : undefined}>
          {active ? 'Filter applied — click to clear' : 'Click to filter'}
        </div>
      )}
      {onClick && !filterHint && <div className="stat-card-link">Click to view →</div>}
    </div>
  );
};

export const ActivityCard = ({ 
  user, 
  action, 
  timestamp, 
  status = 'default',
  icon: Icon = null
}) => {
  const statusColors = {
    success: '#10b981',
    pending: '#f59e0b',
    failed: '#ef4444',
    default: '#06b6d4',
  };

  return (
    <Card className="activity-card" size="sm">
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        {Icon ? (
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: statusColors[status],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            flexShrink: 0,
          }}>
            <Icon size={18} />
          </div>
        ) : (
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: statusColors[status],
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--fs-base)',
            fontWeight: '600',
            flexShrink: 0,
          }}>
            {user?.charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: '500', color: 'var(--text-primary)' }}>
            {user}
          </p>
          <p style={{ 
            margin: '4px 0 0 0',
            fontSize: 'var(--fs-base)',
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {action}
          </p>
          <p style={{ 
            margin: '4px 0 0 0',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-light)'
          }}>
            {timestamp}
          </p>
        </div>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: statusColors[status],
          flexShrink: 0,
          marginTop: '8px',
        }} />
      </div>
    </Card>
  );
};

export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  loading = false,
  className = '',
  ...props 
}) => {
  const variantClasses = {
    primary: 'btn btn-primary',
    secondary: 'btn btn-secondary',
    danger: 'btn btn-danger',
    outline: 'btn btn-outline',
    ghost: 'btn btn-ghost',
  };

  const sizeClasses = {
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
  };

  return (
    <button 
      className={`
        ${variantClasses[variant]} 
        ${sizeClasses[size]}
        ${loading ? 'loading' : ''}
        ${className}
      `}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? '...' : children}
    </button>
  );
};

export const Badge = ({ 
  children, 
  variant = 'primary',
  className = '',
  ...props 
}) => {
  const variantClasses = {
    primary: 'badge badge-primary',
    success: 'badge badge-success',
    danger: 'badge badge-danger',
    warning: 'badge badge-warning',
    info: 'badge badge-info',
  };

  return (
    <span 
      className={`${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </span>
  );
};

export const PageHeader = ({ 
  title, 
  subtitle = null,
  icon: Icon = null,
  action = null,
  animated = false
}) => {
  return (
    <div 
      className={`page-header ${animated ? 'animate-slide-down' : ''}`}
      style={{
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        borderBottom: '1px solid var(--border-light)',
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {Icon && (
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'var(--primary-ultralight)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary-color)',
              fontSize: 'var(--fs-2xl)',
            }}>
              <Icon />
            </div>
          )}
          <div>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--fs-3xl)',
              fontWeight: '700',
              color: 'var(--text-primary)'
            }}>
              {title}
            </h1>
            {subtitle && (
              <p style={{
                margin: '0.5rem 0 0 0',
                color: 'var(--text-secondary)',
                fontSize: 'var(--fs-base)'
              }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {action && <div>{action}</div>}
      </div>
    </div>
  );
};

export const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = {
    sm: '24px',
    md: '40px',
    lg: '60px',
  };

  return (
    <div style={{
      width: sizes[size],
      height: sizes[size],
      border: '3px solid var(--border-light)',
      borderTop: '3px solid var(--primary-color)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto',
    }} />
  );
};

export const EmptyState = ({ 
  icon: Icon = null,
  title = 'No data',
  description = null,
  action = null
}) => {
  return (
    <Card className="empty-state" style={{
      textAlign: 'center',
      padding: '3rem 2rem',
      background: 'linear-gradient(135deg, var(--primary-ultralight) 0%, var(--card-bg) 100%)',
      border: '2px dashed var(--border-light)',
    }}>
      {Icon && (
        <div style={{
          fontSize: 'var(--fs-3xl)',
          color: 'var(--primary-color)',
          marginBottom: '1rem',
          opacity: 0.5,
        }}>
          <Icon />
        </div>
      )}
      <h3 style={{
        margin: '0 0 0.5rem 0',
        color: 'var(--text-primary)',
        fontSize: 'var(--fs-xl)',
        fontWeight: '600',
      }}>
        {title}
      </h3>
      {description && (
        <p style={{
          margin: 0,
          color: 'var(--text-secondary)',
          fontSize: 'var(--fs-base)',
          marginBottom: '1.5rem',
        }}>
          {description}
        </p>
      )}
      {action && <div>{action}</div>}
    </Card>
  );
};
